import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Card from 'components/Shared/Card'
import FunnelChartComp from 'components/Shared/Charts/FunnelChart'
import { VisitorEngagement as VisitorEngagementType } from 'types/VisitorEngagement'
import chartColors from 'assets/styles/scss/shared/_chart-colors.module.scss'
import CardSkeleton from 'components/Shared/SkeletonStack/Card/CardSkeleton'
import NoDataComp from 'components/Shared/NoData'
import { useAppSelector } from 'redux/Hook'

type visitorEngagementProps = {
  visitorEngagementData: VisitorEngagementType
  isLoading: boolean
}

interface MoreVertActionsProps {
  moreVertOptions: string[]
  moreVertRef: React.RefObject<HTMLDivElement>
  fileNameKey: string
}

const getColors = () => {
  const { iris, flame, seaGreen } = chartColors

  return [iris, flame, seaGreen]
}

const dataFormatter = (data: any[]) => {
  const colors = getColors()

  const dataWithColors = data?.map(
    (point: VisitorEngagementType, index: number) => {
      return {
        ...point,
        fill: colors[index],
      }
    },
  )

  return dataWithColors
}

const VisitorEngagementCard = ({
  visitorEngagementData,
  isLoading,
}: visitorEngagementProps) => {
  const { t } = useTranslation()
  const {
    stakeholderDashboardDetails: { accessToken = '' },
  }: any = useAppSelector((state) => state)

  // create visitors engagement ref
  const createMoreVertRef = useRef<HTMLDivElement>(
    null as unknown as HTMLDivElement,
  )

  const moreVertProps: MoreVertActionsProps = {
    moreVertOptions: ['jpeg', 'png', 'pdf', 'csv'],
    moreVertRef: createMoreVertRef,
    fileNameKey: 'visitorsEngagement',
  }

  const dataWithColors = dataFormatter(visitorEngagementData?.data)

  const buildSkeleton = (
    <CardSkeleton showHeaderSkeleton={false} sectionCount={1} height={300} />
  )

  const checkIfDataExists = () => {
    const data = visitorEngagementData?.data

    if (data?.length > 0) return true
    return false
  }

  const getDataComp = () => {
    const ifData = checkIfDataExists()

    if (ifData) {
      return (
        <FunnelChartComp
          data={dataWithColors}
          dataKey="value"
          labelKey="label"
          title={`${t('reporting.overview.visitorEngagement')}`}
        />
      )
    }

    return <NoDataComp />
  }

  const visitorEngagementChartContent = () => {
    return isLoading ? buildSkeleton : getDataComp()
  }

  return (
    <Card
      info={visitorEngagementData?.info}
      moreVertProps={moreVertProps}
      header={t('reporting.overview.visitorEngagement')}
      topContentSpacing="none"
      content={visitorEngagementChartContent()}
      showMoreAction={checkIfDataExists() && !accessToken}
    />
  )
}

export default VisitorEngagementCard
